import { UseFormSetValue, Path, PathValue } from 'react-hook-form';
import { countBy } from 'lodash-es';
import { parseAbsolute, parseZonedDateTime } from '@internationalized/date';
import {
  AirportLocationSchemaType,
  BaseAviaLegSchemaValues,
  CommonLegResourcesSchemaValues,
  CommonRouteLegSchemaValues,
  DEFAULT_AIRPORT_LOCATION_VALUES,
  DEFAULT_CUSTOM_LOCATION_VALUES,
  DEFAULT_FACILITY_LOCATION_VALUES,
  LocationSchemaType,
} from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import {
  AirportLegLocationReference,
  AirportRouteLocation,
  CustomAddressLegLocationReference,
  CustomAddressRouteLocation,
  GroundLocationReference,
  HomeLocationRouteLocation,
  HospitalRouteLocation,
  HubRouteLocation,
  LegLocationTypeEnum,
  LogisticsProjectionType,
  PredefinedPlaceType,
  RouteLegTypeEnum,
  RouteLocationType,
  TxCenterRouteLocation,
} from '@/api/apis/route';
import { getLocationAddress } from '@/utils/helpers';
import { queryClient } from '@/queries';
import { routePlannerQueryKeys } from '@/queries/route-planner/route-planner.keys';

const LOCATION_TYPE_TO_FIELDS_MAP: Partial<Record<LegLocationTypeEnum, LocationSchemaType>> = {
  [LegLocationTypeEnum.custom]: DEFAULT_CUSTOM_LOCATION_VALUES,
  [LegLocationTypeEnum.airport]: DEFAULT_AIRPORT_LOCATION_VALUES,
  [LegLocationTypeEnum.hub]: DEFAULT_FACILITY_LOCATION_VALUES,
  [LegLocationTypeEnum.txcenter]: {
    ...DEFAULT_FACILITY_LOCATION_VALUES,
    locationType: LegLocationTypeEnum.txcenter,
    locationTypeId: LegLocationTypeEnum.txcenter,
  },
  [LegLocationTypeEnum.hospital]: {
    ...DEFAULT_FACILITY_LOCATION_VALUES,
    locationType: LegLocationTypeEnum.hospital,
    locationTypeId: LegLocationTypeEnum.hospital,
  },
  [LegLocationTypeEnum.homeResidence]: {
    ...DEFAULT_FACILITY_LOCATION_VALUES,
    locationType: LegLocationTypeEnum.homeResidence,
    locationTypeId: LegLocationTypeEnum.homeResidence,
  },
};

export const FACILITY_PLACE_TYPES = [LegLocationTypeEnum.hospital, LegLocationTypeEnum.txcenter];

const FACILITY_PLACE_LABELS: Partial<Record<LegLocationTypeEnum, { single: string; prefix: string }>> = {
  [LegLocationTypeEnum.hospital]: { single: 'Donor Hospital', prefix: 'DH' },
  [LegLocationTypeEnum.txcenter]: { single: 'TX Center', prefix: 'TXC' },
};

/**
 * Generic DH/TXC label is used while there is only one entity of the type, otherwise each one is named by its code.
 */
export const labelFacilityPlaces = (
  places: PredefinedPlaceType[],
  getLocationById: (locationId?: string) => RouteLocationType | undefined,
): PredefinedPlaceType[] => {
  const countByType = countBy(places, 'type');

  return places.map((place) => {
    const labels = FACILITY_PLACE_LABELS[place.type];

    if (!labels) return place;

    if (countByType[place.type] <= 1) {
      return { ...place, label: labels.single };
    }

    const location = getLocationById(place.id) as HospitalRouteLocation | TxCenterRouteLocation | undefined;

    return { ...place, label: `${labels.prefix} ${location?.code || location?.name || ''}`.trim() };
  });
};

export const resetLocationFields = <T extends CommonRouteLegSchemaValues>(
  location: keyof T,
  locationType: LegLocationTypeEnum,
  setValue: UseFormSetValue<T>,
) => {
  const newLocationField = LOCATION_TYPE_TO_FIELDS_MAP[locationType] || null;

  if (!newLocationField) return;

  setValue(location as Path<T>, newLocationField as PathValue<T, Path<T>>);
};

export const getAviaLegLocationData = (
  location: AirportLegLocationReference,
  locationsList: RouteLocationType[],
): LocationSchemaType => {
  const airport = locationsList.find((loc): loc is AirportRouteLocation => loc.id === location.locationId);

  if (!airport) {
    return DEFAULT_AIRPORT_LOCATION_VALUES;
  }

  return {
    ...DEFAULT_AIRPORT_LOCATION_VALUES,
    id: location.airportId,
    locationType: location.locationType,
    displayName: airport.iata || airport.icao || airport.name || '',
    locationTypeId: LegLocationTypeEnum.airport,
    fboId: location.fboReference?.fboLocationId || '',
    fboAddressId: location.fboReference?.fboAddressId || 0,
    longitude: airport.location.longitude,
    latitude: airport.location.latitude,
    maoId: `${airport.maoId}`,
    timezone: airport.location.timezone,
  };
};

export const getGroundLegLocationData = (
  location: GroundLocationReference,
  locationsList: RouteLocationType[],
): LocationSchemaType => {
  switch (location.locationType) {
    case LegLocationTypeEnum.airport:
      return getAviaLegLocationData(location.airportReference, locationsList);
    case LegLocationTypeEnum.hub:
    case LegLocationTypeEnum.hospital:
    case LegLocationTypeEnum.txcenter:
    case LegLocationTypeEnum.homeResidence:
      const locationData = locationsList.find(
        (loc): loc is HomeLocationRouteLocation | HospitalRouteLocation | TxCenterRouteLocation | HubRouteLocation =>
          loc.id === location.locationId,
      );

      if (!locationData) {
        return DEFAULT_FACILITY_LOCATION_VALUES;
      }

      return {
        ...DEFAULT_FACILITY_LOCATION_VALUES,
        id: location.locationId,
        locationType: location.locationType,
        locationTypeId: `${location.locationType}|${location.locationId}`,
        longitude: locationData.location.longitude,
        latitude: locationData.location.latitude,
        timezone: locationData.location.timezone,
      };
    case LegLocationTypeEnum.custom:
      const customAddress = locationsList.find(
        (loc): loc is CustomAddressRouteLocation => loc.id === location.locationId,
      );

      if (!customAddress) {
        return DEFAULT_CUSTOM_LOCATION_VALUES;
      }

      return {
        ...DEFAULT_CUSTOM_LOCATION_VALUES,
        displayName: getLocationAddress(customAddress.location),
        locationTypeId: LegLocationTypeEnum.custom,
        id: location.locationId,
        hereMapsId: customAddress.hereMapsId,
        locationType: LegLocationTypeEnum.custom,
        country: customAddress.location.country,
        addressLine: customAddress.location.addressLine,
        state: customAddress.location.state,
        city: customAddress.location.city,
        postalCode: customAddress.location.postalCode,
        latitude: customAddress.location.latitude,
        longitude: customAddress.location.longitude,
        timezone: customAddress.location.timezone,
      };
    default:
      return DEFAULT_AIRPORT_LOCATION_VALUES;
  }
};

export const getAccommodationLegLocationData = (
  location: CustomAddressLegLocationReference | null,
  locationsList: RouteLocationType[],
): LocationSchemaType => {
  if (!location) {
    return DEFAULT_CUSTOM_LOCATION_VALUES;
  }

  const customAddress = locationsList.find((loc): loc is CustomAddressRouteLocation => loc.id === location.locationId);

  if (!customAddress) {
    return DEFAULT_CUSTOM_LOCATION_VALUES;
  }

  return {
    ...DEFAULT_CUSTOM_LOCATION_VALUES,
    displayName: getLocationAddress(customAddress.location),
    locationTypeId: LegLocationTypeEnum.custom,
    locationType: LegLocationTypeEnum.custom,
    id: location.locationId,
    hereMapsId: customAddress.hereMapsId,
    country: customAddress.location.country,
    addressLine: customAddress.location.addressLine,
    state: customAddress.location.state,
    city: customAddress.location.city,
    postalCode: customAddress.location.postalCode,
    latitude: customAddress.location.latitude,
    longitude: customAddress.location.longitude,
    timezone: customAddress.location.timezone,
  };
};

export const getLocationData = (
  logisticsId: string,
  legType: RouteLegTypeEnum,
  location: AirportLegLocationReference | GroundLocationReference | CustomAddressLegLocationReference | null,
): LocationSchemaType => {
  const data = queryClient.getQueryData<LogisticsProjectionType>(routePlannerQueryKeys.getLogistics(logisticsId));
  const { locations } = data || {};
  const hasLocations = locations?.length;

  if (!hasLocations) {
    return DEFAULT_AIRPORT_LOCATION_VALUES;
  }

  switch (legType) {
    case RouteLegTypeEnum.TmdxJetAviaLeg:
    case RouteLegTypeEnum.SupplementalLiftAviaLeg:
    case RouteLegTypeEnum.TxCenterProviderAviaLeg:
    case RouteLegTypeEnum.CommercialFlightAviaLeg:
      return getAviaLegLocationData(location as AirportLegLocationReference, locations);
    case RouteLegTypeEnum.TmdxAffiliateGroundLeg:
    case RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg:
    case RouteLegTypeEnum.TmdxDriveTedsGroundLeg:
    case RouteLegTypeEnum.TxCenterProviderGroundLeg:
    case RouteLegTypeEnum.CurriServiceGroundLeg:
    case RouteLegTypeEnum.SelfServicedGroundLeg:
      return getGroundLegLocationData(location as GroundLocationReference, locations);
    case RouteLegTypeEnum.AccommodationLeg:
      return getAccommodationLegLocationData(location as CustomAddressLegLocationReference | null, locations);
    default:
      return DEFAULT_AIRPORT_LOCATION_VALUES as LocationSchemaType;
  }
};

// TODO: fix durations on leg type change
export const getDateTimeInLocationTz = (
  currentValues: CommonLegResourcesSchemaValues,
  durationData?: Record<string, number>,
) => {
  const timeDurationInMs =
    durationData && durationData.timeDurationInMs !== currentValues.timeDurationInMs
      ? durationData.timeDurationInMs
      : currentValues.timeDurationInMs;
  const arrivalTimeUtcString = parseZonedDateTime(currentValues.departureDateTime)
    .add({ milliseconds: timeDurationInMs })
    .toAbsoluteString();
  return parseAbsolute(arrivalTimeUtcString, currentValues.arrivalLocation.timezone || 'UTC');
};

export const getAirportLocationValues = (
  currentValues: CommonLegResourcesSchemaValues,
  defaultValues?: BaseAviaLegSchemaValues,
  durationData?: Record<string, number>,
): Partial<BaseAviaLegSchemaValues> => {
  const arrivalTimeStringInLocationTz = getDateTimeInLocationTz(currentValues, durationData);
  const isDepartureAirport = currentValues.departureLocation.locationType === LegLocationTypeEnum.airport;
  const hasDepartureId = !!currentValues.departureLocation.id;
  const isArrivalAirport = currentValues.arrivalLocation.locationType === LegLocationTypeEnum.airport;
  const hasArrivalId = !!currentValues.arrivalLocation.id;

  const isDefaultDepartureAirport = currentValues.departureLocation.id === defaultValues?.departureLocation.id;

  return {
    timeDurationInMs: durationData?.timeDurationInMs || 0,
    ...(!isDefaultDepartureAirport && isDepartureAirport && hasDepartureId
      ? {
          departureLocation: currentValues.departureLocation as AirportLocationSchemaType,
          departureDateTime: currentValues.departureDateTime,
          departureDateTimeValue: currentValues.departureDateTimeValue,
          departureLocalTimezone: currentValues.departureLocalTimezone,
        }
      : {}),
    ...(isArrivalAirport && hasArrivalId
      ? {
          arrivalLocation: currentValues.arrivalLocation as AirportLocationSchemaType,
          arrivalDateTime: arrivalTimeStringInLocationTz.toString(),
          arrivalDateTimeValue: arrivalTimeStringInLocationTz,
          arrivalLocalTimezone: currentValues.arrivalLocalTimezone,
        }
      : {}),
  };
};
